import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Empty, Modal, Spin, Typography, message } from 'antd';
import {
  getVersionTrace,
  getVersionTraceRoot,
  type VersionTrace,
  type VersionTraceLayer,
  type VersionTraceRoot,
} from '@apis/simulation';

/**
 * 算法版本「基准版本」递归下钻弹窗。
 *
 * 交互（详见 docs/algorithm-version-recursive-traceback.md）：
 * - 传入一个入口 `version`，调 `getVersionTrace` 拿**单次一层**详情，展示该版本各仓
 *   commit/分支、完整包路径、基准版本号。
 * - 该层 `hasMore`（基线仍是平台包、可续查）时，基准版本号显示「继续追溯 →」：点击以
 *   `baseVersionNorm` 为新起点再查一层，结果**追加到下方堆叠展示**（每层一块），
 *   整体在弹窗内限高滚动；加载完成自动滚到新层。
 * - 基线是外部/打板基线（查不到平台包）→ 基准版本号为纯文本，链到此为止。
 * - 点击已在链中展示过的基线 → 滚动定位并高亮，不重复追加。
 * - 入口版本不可追溯（外部/打板包）→ 展示灰字提示，不阻断。
 */
export interface VersionTraceModalProps {
  open: boolean;
  /** 追溯入口版本号（后端归一化好的 traceEntryVersion）。open=true 时必填。 */
  version?: string | null;
  onClose: () => void;
  /** 叠放层级。默认不传 → 用 antd 默认 1000（页面级用法：追溯窗是唯一弹窗）。
   *  从别的 Modal 里打开追溯时必须传更大值：antd 同 zIndex 的 Modal 靠 portal
   *  在 body 中的先后决定叠放，而带 destroyOnClose 的弹窗每次重开都会把自己的
   *  portal 重新 append 到末尾，从而盖住先挂载的追溯窗（表现为「第二次才复现」）。 */
  zIndex?: number;
}

/** 面包屑一段：一次接口调用的起点版本 + 其返回结果（懒加载后缓存，回跳零延迟）。 */
interface TraceSegment {
  entry: string;
  data?: VersionTrace;
  loading: boolean;
  error?: string;
}

/** 单仓 commit 行：`module  branch (commit12)`；无分支时只显示 commit。 */
const ModuleCommitRow: React.FC<{
  module: string;
  branch?: string | null;
  commit?: string | null;
}> = ({ module, branch, commit }) => (
  <div style={{ display: 'flex', gap: 8, fontSize: 12, lineHeight: 1.9 }}>
    <span style={{ fontWeight: 500, minWidth: 130, color: '#333', wordBreak: 'break-all' }}>
      {module}
    </span>
    <span style={{ color: '#666', wordBreak: 'break-all' }}>
      {branch ? (
        <>
          {branch}
          {commit && <span style={{ color: '#999' }}> ({commit.slice(0, 12)})</span>}
        </>
      ) : (
        commit ? commit.slice(0, 12) : '-'
      )}
    </span>
  </div>
);

/** 一层追溯卡：完整包路径 → 模块分支/Commit → 基准版本号（可点）。 */
const LayerCard: React.FC<{
  layer: VersionTraceLayer;
  index: number;
  /** 点击本层基准版本号：链内定位（有下一层）或续查（hasMore）。 */
  onClickBase: (layer: VersionTraceLayer) => void;
  /** 供层内定位滚动的 ref 注册。 */
  registerRef: (version: string, el: HTMLDivElement | null) => void;
  highlighted: boolean;
}> = ({ layer, index, onClickBase, registerRef, highlighted }) => {
  const modules = Object.entries(layer.moduleCommits || {}).sort(([a], [b]) => a.localeCompare(b));
  return (
    <div
      ref={(el) => registerRef(layer.version, el)}
      style={{
        marginBottom: 12,
        padding: '10px 12px',
        border: '1px solid #eee',
        borderRadius: 6,
        background: highlighted ? '#f0f5ff' : '#fafafa',
        transition: 'background 0.4s',
      }}
    >
      <div style={{ fontWeight: 600, color: '#333', marginBottom: 6 }}>
        <span style={{ color: '#5e6ad2' }}>第 {index + 1} 层</span>
        <span style={{ marginLeft: 8, wordBreak: 'break-all' }}>{layer.version}</span>
      </div>

      {layer.packageUrl && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 4 }}>
          <span style={{ fontWeight: 500, minWidth: 88, color: '#333' }}>完整包路径:</span>
          <Typography.Text
            copyable={{
              text: layer.packageUrl,
              tooltips: ['复制完整包路径', '已复制'],
              onCopy: () => message.success('已复制完整包路径'),
            }}
            style={{ color: '#666', flex: 1, minWidth: 0 }}
          >
            <code style={{ wordBreak: 'break-all', fontSize: 12 }}>{layer.packageUrl}</code>
          </Typography.Text>
        </div>
      )}

      <div style={{ marginBottom: 4 }}>
        <div style={{ fontWeight: 500, color: '#333', marginBottom: 2 }}>模块分支 / Commit:</div>
        {modules.length === 0 ? (
          <div style={{ fontSize: 12, color: '#999', fontStyle: 'italic' }}>无模块 commit 记录</div>
        ) : (
          modules.map(([module, info]) => (
            <ModuleCommitRow key={module} module={module} branch={info.branch} commit={info.commit} />
          ))
        )}
      </div>

      {layer.baseVersion && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginTop: 4 }}>
          <span style={{ fontWeight: 500, minWidth: 88, color: '#333' }}>基准版本号:</span>
          {layer.hasMore && layer.baseVersionNorm ? (
            // 基线仍是平台包（可续查）→ 可点击「继续追溯」再查一层
            <a
              style={{ color: '#5e6ad2', wordBreak: 'break-all' }}
              onClick={() => onClickBase(layer)}
            >
              {layer.baseVersion}
              <span style={{ color: '#999', fontStyle: 'normal' }}> （继续追溯 →）</span>
            </a>
          ) : (
            // 基线是外部 / 打板基线（查不到平台包）→ 纯文本，链到此为止
            <span style={{ color: '#666', wordBreak: 'break-all' }}>{layer.baseVersion}</span>
          )}
        </div>
      )}
    </div>
  );
};

/** 顶部「追到底」汇总：最原始平台版本 + 全部仓库变化项（各层改动并集）。
 *  与逐层下钻互补——一眼看到「当前任务依赖的最原始版本」及两者之间动过的仓库。 */
const RootSummary: React.FC<{ root: VersionTraceRoot | null; loading: boolean }> = ({
  root, loading,
}) => {
  const [expanded, setExpanded] = useState(false);
  if (loading) {
    return (
      <div style={{ padding: '8px 12px', marginBottom: 12, background: '#f6f8ff', borderRadius: 6 }}>
        <Spin size="small" /> <span style={{ color: '#888', fontSize: 12 }}>正在追溯最原始版本…</span>
      </div>
    );
  }
  // 入口即外部包 / 没追到平台包 → 不展示汇总（逐层区已有相应提示）
  if (!root || !root.traceable || !root.rootVersion) return null;

  const mods = root.changedModules || [];
  return (
    <div style={{ padding: '10px 12px', marginBottom: 12, background: '#f6f8ff', borderRadius: 6 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600, color: '#333' }}>最原始版本:</span>
        <Typography.Text
          copyable={{ text: root.rootVersion, tooltips: ['复制', '已复制'] }}
          style={{ color: '#5e6ad2' }}
        >
          <code style={{ wordBreak: 'break-all', fontSize: 12 }}>{root.rootVersion}</code>
        </Typography.Text>
        <span style={{ color: '#999', fontSize: 12 }}>（共 {root.depth} 跳</span>
        {root.rootBaseVersion && (
          <span style={{ color: '#999', fontSize: 12 }}>· 源头基线 {root.rootBaseVersion}）</span>
        )}
        {!root.rootBaseVersion && <span style={{ color: '#999', fontSize: 12 }}>）</span>}
        {mods.length > 0 && (
          <a style={{ color: '#5e6ad2', fontSize: 12 }} onClick={() => setExpanded((v) => !v)}>
            {expanded ? '收起变化项' : `查看全部变化项（${mods.length} 个仓库）`}
          </a>
        )}
      </div>

      {expanded && mods.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, lineHeight: 1.9 }}>
          <div style={{ color: '#888', marginBottom: 4 }}>
            当前 → 最原始版本之间，各仓库的最终改动（标注来自哪一层）：
          </div>
          {mods.map((m) => (
            <div key={m.module} style={{ display: 'flex', gap: 8 }}>
              <span style={{ fontWeight: 500, minWidth: 130, color: '#333', wordBreak: 'break-all' }}>
                {m.module}
              </span>
              <span style={{ color: '#666', wordBreak: 'break-all' }}>
                {m.branch ? (
                  <>
                    {m.branch}
                    {m.commit && <span style={{ color: '#999' }}> ({m.commit.slice(0, 12)})</span>}
                  </>
                ) : (
                  m.commit ? m.commit.slice(0, 12) : '-'
                )}
                {m.sourceVersion && (
                  <span style={{ color: '#999' }}> （来自 {m.sourceVersion}）</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/** 追溯不可用 / 空态提示文案（按 stopReason 区分外部包与数据缺失）。 */
const traceEmptyHint = (data?: VersionTrace): string => {
  if (!data) return '';
  if (data.stopReason === 'not_found') return '外部 / 打板包，无法追溯各仓 commit';
  return '未追溯到任何平台编译记录';
};

const VersionTraceModal: React.FC<VersionTraceModalProps> = ({ open, version, onClose, zIndex }) => {
  // 面包屑栈：每段是一次接口调用（一个起点 + 其一层结果）。回跳只截断栈，命中已加载缓存。
  const [segments, setSegments] = useState<TraceSegment[]>([]);
  // 最新 segments 的 ref：供 handleClickBase 判断基线是否已在链中而不必把 segments 进 deps。
  const segmentsRef = useRef(segments);
  segmentsRef.current = segments;
  const [highlightVersion, setHighlightVersion] = useState<string | null>(null);
  const layerRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  // 顶部「追到底」汇总：只基于最初入口版本，不随面包屑续查变化。
  const [root, setRoot] = useState<VersionTraceRoot | null>(null);
  const [rootLoading, setRootLoading] = useState(false);

  const loadSegment = useCallback(async (entry: string, segIndex: number) => {
    try {
      const data = await getVersionTrace(entry);
      setSegments((prev) => {
        const next = [...prev];
        if (next[segIndex] && next[segIndex].entry === entry) {
          next[segIndex] = { entry, data, loading: false };
        }
        return next;
      });
      // 续查（非首段）加载完成 → 下一帧滚到新追加的这层，省得用户手动往下拖。
      if (segIndex > 0 && data.layers.length > 0) {
        const firstVersion = data.layers[0].version;
        window.setTimeout(() => {
          layerRefs.current.get(firstVersion)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 60);
      }
    } catch (e) {
      setSegments((prev) => {
        const next = [...prev];
        if (next[segIndex] && next[segIndex].entry === entry) {
          next[segIndex] = { entry, loading: false, error: '追溯失败，请重试' };
        }
        return next;
      });
    }
  }, []);

  // open 或入口 version 变化：重置为单段并加载；同时拉一次「追到底」汇总。
  useEffect(() => {
    if (!open || !version) {
      setSegments([]);
      setHighlightVersion(null);
      layerRefs.current.clear();
      setRoot(null);
      setRootLoading(false);
      return;
    }
    setSegments([{ entry: version, loading: true }]);
    setHighlightVersion(null);
    layerRefs.current.clear();
    void loadSegment(version, 0);

    // 汇总只基于最初入口版本；用 cancelled 守卫防 version 快速切换时旧结果覆盖新的。
    let cancelled = false;
    setRoot(null);
    setRootLoading(true);
    getVersionTraceRoot(version)
      .then((r) => {
        if (!cancelled) setRoot(r);
      })
      .catch(() => {
        if (!cancelled) setRoot(null);  // 汇总失败静默（逐层区仍可用），不弹错
      })
      .finally(() => {
        if (!cancelled) setRootLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, version, loadSegment]);

  const registerRef = useCallback((v: string, el: HTMLDivElement | null) => {
    if (el) layerRefs.current.set(v, el);
    else layerRefs.current.delete(v);
  }, []);

  /** 点击某层基准版本号：基线已在链中某层 → 滚动定位高亮；否则（hasMore 链尾）→ 续查追加下一层。 */
  const handleClickBase = useCallback(
    (layer: VersionTraceLayer, segIndex: number) => {
      const baseNorm = layer.baseVersionNorm;
      if (!baseNorm) return;
      // 基线是否已在**任何**已展示段里（堆叠模式下所有层都在页面上）→ 定位高亮，不重复追加。
      const alreadyShown = segmentsRef.current.some(
        (s) => s.data?.layers.some((l) => l.version === baseNorm),
      );
      if (alreadyShown) {
        setHighlightVersion(baseNorm);
        const el = layerRefs.current.get(baseNorm);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        window.setTimeout(() => setHighlightVersion((cur) => (cur === baseNorm ? null : cur)), 1500);
        return;
      }
      // 续查：在当前段之后追加新段（往下堆叠一层）。
      setSegments((prev) => {
        const trimmed = prev.slice(0, segIndex + 1);
        return [...trimmed, { entry: baseNorm, loading: true }];
      });
      void loadSegment(baseNorm, segIndex + 1);
    },
    [loadSegment],
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={640}
      title="算法版本追溯"
      zIndex={zIndex}
      styles={{ body: { maxHeight: '70vh', overflow: 'auto' } }}
    >
      <RootSummary root={root} loading={rootLoading} />

      {segments.length === 0 ? null : (
        <>
          {segments.map((seg, segIndex) => {
            // 该段第一层的全局层号基数 = 前序各段已展示的层数之和（堆叠累积、不重置）。
            const layerBase = segments
              .slice(0, segIndex)
              .reduce((sum, s) => sum + (s.data?.layers.length ?? 0), 0);

            if (seg.loading) {
              // 续查中：前序层保持堆叠，底部显示转圈（往下追的加载反馈）。
              return (
                <div key={`seg-${segIndex}-loading`} style={{ textAlign: 'center', padding: '16px 0' }}>
                  <Spin size="small" /> <span style={{ color: '#888', fontSize: 12 }}>追溯中…</span>
                </div>
              );
            }
            if (seg.error) {
              return (
                <Empty
                  key={`seg-${segIndex}-error`}
                  description={seg.error}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              );
            }
            if (seg.data && seg.data.layers.length > 0) {
              return seg.data.layers.map((layer, idx) => (
                <LayerCard
                  key={`${layer.version}-${segIndex}-${idx}`}
                  layer={layer}
                  index={layerBase + idx}
                  highlighted={highlightVersion === layer.version}
                  registerRef={registerRef}
                  onClickBase={(l) => handleClickBase(l, segIndex)}
                />
              ));
            }
            // 仅首段无结果 → 外部包 / 无追溯提示；续查段理论不为空（hasMore 才能续查），静默。
            return segIndex === 0 ? (
              <Empty
                key="seg-0-empty"
                description={traceEmptyHint(seg.data)}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : null;
          })}
        </>
      )}
    </Modal>
  );
};

export default VersionTraceModal;
